/**
 * F4 · পেমেন্ট মেথড — `/account/payment`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame F4 (360 × 680).
 *
 * Frame note: "Cash on delivery is listed as a real method, not a fallback — it is still
 * how a large share of orders are paid, and burying it insults that user." So
 * `হাতে হাতে দেব` is a row under its own heading, in the position the frame gives it,
 * with its state written next to it. It is not appended after the "real" methods and it
 * is not styled down.
 *
 * This is a money screen, and the partner disclosure it draws is the one the flow uses
 * everywhere: অপ্রাপ্য ক্যাশ is operated by a partner institution and Oprappo is not
 * itself a bank. No frame draws a slot for the payload's `partner_name`, and it is not
 * invented here — see the note on the disclosure block below.
 *
 * WIRED. `GET /v1/account/payment-methods` supplies the অপ্রাপ্য ক্যাশ block, the
 * disclosure, both headings, every card and every other method. The disclosure now comes
 * from the SERVER rather than from `../offering/chrome.js`, and that matters on this
 * screen more than on most: docs/05 § fulfilment makes the disclosure a regulatory
 * requirement enforced in code, and a client constant is a requirement one build can
 * silently drop.
 *
 * THE CARD ROWS' TWO LINES ARE SERVER STRINGS. `কার্ড · **** ৪৮২১` and `মেয়াদ ০৯ / ২৮`
 * are composed in `apps/api/src/account/copy.ts` from brand, last four and expiry — the
 * only three facts stored — and a test there reproduces the frame's own line character
 * for character. Nothing about a card is composed here, and no payload on this screen can
 * carry a PAN: there is no field for one in the contract, no column for one in 0021, and
 * the route refuses an unknown field rather than dropping it.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@oprappo/ui';
import { formatTaka } from '@oprappo/contracts';
import type { PaymentMethodsResponse } from '@oprappo/contracts';
import { Frame, PlusIcon, SAND_FRAME, ScreenHeader } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchPaymentMethods } from './api.js';

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
export function F4PaymentView({ data: p }: { data: PaymentMethodsResponse | null }): JSX.Element {
  const navigate = useNavigate();

  return (
    <Frame className={SAND_FRAME}>
      <ScreenHeader titleBn={p?.title_bn ?? ''} onBack={() => navigate('/account')} />

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div className="bg-white border border-border p-[15px] mb-[18px]">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-anek font-medium text-[15.5px] leading-[1.75] text-ink">
                {p?.cash.label_bn ?? ''}
              </div>
              <div className="font-manrope font-bold text-[22px] leading-[1.15] text-ink tabular-nums mt-[5px]">
                {p === null ? '' : formatTaka(p.cash.amount_minor)}
              </div>
            </div>
            <button
              type="button"
              className="border-[1.5px] border-teal px-[14px] py-[10px] font-anek font-semibold text-[14px] leading-[1.75] text-teal flex-none rounded-none bg-transparent"
            >
              {p?.cash.add_bn ?? ''}
            </button>
          </div>
          {/*
           * ONE line of `400 13px/1.75` body text, as the frame draws it.
           * The payload's `partner_name` was rendered here as a second line; the frame
           * draws no such line, and that extra 22.75px pushed the card headings, both
           * method rows and the dashed add-card box down by a full line — it was the
           * whole of this screen's diff. It also disclosed nothing extra: the fixture's
           * `partner_name` is the very phrase this sentence already contains
           * (অংশীদার প্রতিষ্ঠান), so docs/05 § fulfilment is satisfied by the drawn
           * string itself. D5 removed the same duplicate line for the same reason.
           */}
          <div className="border-t border-border mt-[13px] pt-[11px] font-anek font-normal text-[13px] leading-[1.75] text-body">
            {p?.disclosure_bn ?? ''}
          </div>
        </div>

        <div className="font-anek font-semibold text-[15px] leading-[1.75] text-muted mb-[11px]">
          {p?.cards_heading_bn ?? ''}
        </div>
        {(p?.cards ?? []).map((card) => (
          <div
            key={card.id}
            className="bg-white border border-border p-[14px] flex justify-between items-center gap-[11px]"
          >
            <div className="flex-1">
              {/*
                * Both lines are the server's, composed there from brand, last four and
                * expiry — the only three facts this system holds about a card
                * (`apps/api/src/account/copy.ts § cardLabelBn`). There is no PAN in this
                * payload because there is no field for one anywhere in the chain.
                */}
              <div className="font-anek font-medium text-[15.5px] leading-[1.75] text-ink">
                {card.label_bn}
              </div>
              <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
                {card.expiry_bn}
              </div>
            </div>
            <IconButton control="more" size={18} stroke="#5B6B69" className="flex-none" />
          </div>
        ))}

        <div className="border border-dashed border-border-warm bg-sand-faint p-[15px] flex gap-[12px] items-center mt-[11px]">
          <PlusIcon size={20} />
          <button
            type="button"
            className="flex-1 text-left font-anek font-semibold text-[15.5px] leading-[1.75] text-teal bg-transparent border-0 p-0 rounded-none"
          >
            {p?.add_card_bn ?? ''}
          </button>
        </div>

        <div className="font-anek font-semibold text-[15px] leading-[1.75] text-muted mt-[20px] mb-[11px]">
          {p?.other_heading_bn ?? ''}
        </div>
        {(p?.others ?? []).map((other) => (
          <div
            key={other.id}
            className="bg-white border border-border p-[14px] flex justify-between items-center gap-[11px]"
          >
            <div className="flex-1 font-anek font-medium text-[15.5px] leading-[1.75] text-ink">
              {other.label_bn}
            </div>
            <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted flex-none">
              {other.state_bn}
            </div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

export function F4Payment(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchPaymentMethods(signal), []);
  const { data, error } = useResource(load, 'payment-methods');

  return (
    <SurfaceStates surface="account" error={error ?? undefined}>
      <F4PaymentView data={data} />
    </SurfaceStates>
  );
}
